import { useState } from "react";
import { Link } from "react-router-dom";
import { isNewsletterEnabled, subscribe } from "../lib/newsletter";

const DEFAULT_HEADLINE = "Ricevi 1 mail ogni tanto con tip casa e prodotti scelti";
const DEFAULT_LEAD = "routine-casa";

/**
 * Opt-in newsletter (Netlify Blobs). Consent checkbox required, not pre-checked.
 */
export default function NewsletterForm({
  headline = DEFAULT_HEADLINE,
  leadMagnet = DEFAULT_LEAD,
  source = "footer",
  className = "",
}) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | ok | error | duplicate
  const [message, setMessage] = useState("");

  if (!isNewsletterEnabled()) return null;

  async function onSubmit(e) {
    e.preventDefault();
    if (!consent) {
      setStatus("error");
      setMessage("Per iscriverti serve il consenso all’informativa privacy.");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      await subscribe({ email, consent, leadMagnet, source });
      setStatus("ok");
      setMessage("Iscrizione registrata. Grazie — a presto in inbox.");
      setEmail("");
      setConsent(false);
    } catch (err) {
      if (err.status === 409) {
        setStatus("duplicate");
        setMessage("Questo indirizzo è già iscritto.");
      } else {
        setStatus("error");
        setMessage(err.message || "Qualcosa è andato storto. Riprova più tardi.");
      }
    }
  }

  return (
    <div
      className={className}
      data-testid="newsletter-form"
      aria-labelledby="newsletter-heading"
    >
      <p
        id="newsletter-heading"
        className="text-xs uppercase tracking-[0.2em] text-stone2-mid mb-3"
      >
        Newsletter
      </p>
      <p className="text-sm text-stone2-dark leading-relaxed mb-4 max-w-sm">
        {headline}
      </p>
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <label className="block">
          <span className="sr-only">Email</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="la tua email"
            disabled={status === "loading"}
            className="w-full min-h-11 px-3 border border-wood/50 bg-warm text-stone2-dark text-sm placeholder:text-stone2-mid/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-ink"
            data-testid="newsletter-email"
          />
        </label>
        <label className="flex gap-3 items-start text-xs text-stone2-mid leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            disabled={status === "loading"}
            className="mt-0.5 h-4 w-4 accent-sage-ink"
            data-testid="newsletter-consent"
          />
          <span>
            Ho letto l&apos;
            <Link to="/privacy" className="link-under text-sage-ink">
              Informativa privacy
            </Link>{" "}
            e acconsento a ricevere email editoriali (anche con link commerciali; vedi{" "}
            <Link to="/disclosure" className="link-under text-sage-ink">
              Trasparenza
            </Link>
            ). Posso revocare in qualsiasi momento.
          </span>
        </label>
        <button
          type="submit"
          disabled={status === "loading"}
          className="min-h-11 px-4 text-sm bg-sage-ink text-warm hover:opacity-90 disabled:opacity-60 transition-opacity"
          data-testid="newsletter-submit"
        >
          {status === "loading" ? "Invio…" : "Iscriviti"}
        </button>
        {message ? (
          <p
            role="status"
            className={`text-xs ${
              status === "ok" ? "text-sage-ink" : "text-stone2-dark"
            }`}
            data-testid="newsletter-status"
          >
            {message}
          </p>
        ) : null}
      </form>
    </div>
  );
}
